// src/views/FillInTheBlanks.js
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import styles from "./FillInTheBlanks.module.css";
import useGameLogic from "../hooks/useGameLogic";
import GamePopups from "../components/GamePopups";
import FeedbackModal from "../components/FeedbackModal";
import commonGameStyles from "./GameCommon.module.css";
import LoadingSpinner from "../components/LoadingSpinner";
import { usePreloadedData } from "../context/PreloadContext";

/**
 * @typedef {Object} AyahBlank
 * @property {string} ayahText - The ayah text with blanks
 * @property {string[]} blanks - The correct words for blanks (in order)
 * @property {string[]} options - The selectable options
 * @property {string} reference - The ayah reference (e.g., Surah:Ayah)
 * @property {string} [reflection] - Optional short inspiring note shown after answering
 */

// --- Feedback Config ---
const FEEDBACK_FORM_URL =
    "https://docs.google.com/forms/d/e/1FAIpQLSfCYQQ3NJS3nRLoVB9Q1LtELQxQizAD37t1LMwJ_5-ei2dryA/viewform?usp=dialog";
const FEEDBACK_PROMPT_INTERVAL = 3;
const FEEDBACK_PROMPT_DISMISS_DAYS = 7;

const LS_KEY_GAMES_PLAYED = "ftb_games_played_count";
const LS_KEY_LAST_PROMPT_TIME = "ftb_last_prompt_time";
const LS_KEY_DISMISSED_PERMANENTLY = "ftb_dismissed_permanently";
const LS_KEY_FTB_FIRST_COMPLETED = "ftb_first_completed";

function FillInTheBlanks({ inJourney = false, onComplete, gameLength = 10 }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const autoAdvanceTimeoutRef = useRef(null);

    // --- Local State ---
    const [selected, setSelected] = useState([]);
    const [showExplanation, setShowExplanation] = useState(false);
    const [score, setScore] = useState(0);
    const [done, setDone] = useState(false); // Game completion state
    const [isCorrect, setIsCorrect] = useState(false);
    const [timer, setTimer] = useState(30);
    const [showInstructions, setShowInstructions] = useState(true);

    const ACTIVITY_TYPE = "ayah-blanks";
    const { preloadedData } = usePreloadedData();

    // --- Game Logic Hook ---
    const {
        lives,
        showGameOverPopup,
        showStreakPopup,
        streakMessage,
        showEncouragementPopup,
        encouragementMessage,
        showFeedbackModal,
        handleCloseFeedbackModal,
        handleDismissFeedbackPermanently,
        handleCorrectAttempt,
        handleIncorrectAttempt,
        resetGameLogicStates,
        loadingQuestions,
        error,
        loadQuizQuestions,
        questions,
        current,
        setCurrent,
        isGameEnded, // ⬅️ IMPORT isGameEnded for robust journey check
    } = useGameLogic({
        activityType: ACTIVITY_TYPE,
        initialLives: 5,
        gameSpecificEndCondition: done,
        currentScore: score,
        gameLength,
        inJourney,      // Passed to hook
        onComplete,     // Passed to hook for navigation
        feedbackConfig: {
            FEEDBACK_FORM_URL,
            FEEDBACK_PROMPT_INTERVAL,
            FEEDBACK_PROMPT_DISMISS_DAYS,
            LS_KEY_GAMES_PLAYED,
            LS_KEY_LAST_PROMPT_TIME,
            LS_KEY_DISMISSED_PERMANENTLY,
            LS_KEY_FIRST_COMPLETED: LS_KEY_FTB_FIRST_COMPLETED,
        },
        navigate,
    });

    // --- Load questions on mount ---
    useEffect(() => {
        if (!user) return;
        resetGameLogicStates();
        // Use the key produced by PreloadContext ('fillintheblanks') and
        // guard with optional chaining in case preload hasn't populated yet.
        if (preloadedData?.fillintheblanks) {
            loadQuizQuestions(preloadedData.fillintheblanks);
        } else {
            loadQuizQuestions();
        }
    }, [user, loadQuizQuestions, resetGameLogicStates, preloadedData]);

    // --- Start quiz ---
    const startQuiz = useCallback(() => {
        setShowInstructions(false);
        setTimer(30);
    }, []);

    // --- Next question ---
    const handleNext = useCallback(() => {
        setSelected([]);
        setShowExplanation(false);
        setTimer(30);
        setIsCorrect(false);

        if (current + 1 < questions.length) {
            setCurrent((prev) => prev + 1);
        } else {
            setDone(true); // Signal completion
            // The useGameLogic hook will immediately call 'onComplete' if inJourney is true.
        }
    }, [current, questions.length, setCurrent]);

    // --- Timeout logic ---
    const handleTimeout = useCallback(() => {
        if (!questions.length || current >= questions.length) return;
        setShowExplanation(true);
        setIsCorrect(false);
        handleIncorrectAttempt(questions[current]._id);
    }, [handleIncorrectAttempt, questions, current]);

    // --- Timer effect ---
    useEffect(() => {
        if (
            showExplanation ||
            done ||
            loadingQuestions ||
            questions.length === 0 ||
            showInstructions ||
            showGameOverPopup
        )
            return;

        const interval = setInterval(() => {
            setTimer((prev) => {
                if (prev === 1) {
                    clearInterval(interval);
                    handleTimeout();
                    return 30;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [
        current,
        showExplanation,
        done,
        loadingQuestions,
        questions.length,
        showInstructions,
        handleTimeout,
        showGameOverPopup,
    ]);

    // --- Auto advance after explanation ---
    useEffect(() => {
        if (!showExplanation || showGameOverPopup) return;
        autoAdvanceTimeoutRef.current = setTimeout(() => {
            handleNext();
        }, 8000);
        return () => clearTimeout(autoAdvanceTimeoutRef.current);
    }, [showExplanation, handleNext, showGameOverPopup]);

    // --- Select word ---
    const handleSelect = useCallback(
        (word) => {
            if (showGameOverPopup || showExplanation) return;
            if (!questions.length || current >= questions.length) return;

            const maxBlanks = questions[current].blanks.length;
            if (selected.includes(word)) {
                setSelected(selected.filter((w) => w !== word));
            } else {
                if (selected.length < maxBlanks) {
                    setSelected([...selected, word]);
                } else {
                    // Replace the last selected word with the new one
                    setSelected([...selected.slice(0, -1), word]);
                }
            }
        },
        [showGameOverPopup, showExplanation, questions, current, selected]
    );

    // --- Submit answer ---
    const handleSubmit = useCallback(() => {
        if (showExplanation || showGameOverPopup) return;
        if (!questions.length || current >= questions.length) return;

        const currentQ = questions[current];

        // Check if selected words exactly match the correct blanks array (in order)
        const correct =
            selected.length === currentQ.blanks.length &&
            selected.every((word, idx) => word === currentQ.blanks[idx]);

        setScore((prev) => (correct ? prev + 1 : prev));
        setIsCorrect(correct);
        setShowExplanation(true);

        if (correct) {
            handleCorrectAttempt(currentQ._id);
        } else {
            handleIncorrectAttempt(currentQ._id);
        }
    }, [
        selected,
        current,
        questions,
        showExplanation,
        showGameOverPopup,
        handleCorrectAttempt,
        handleIncorrectAttempt,
    ]);

    // --- Early returns ---
    const backgroundStyle = { backgroundImage: `url("/images/blankkk.jpg")` };

    if (!user)
        return (
            <p className={styles.message}>Please log in to use this feature.</p>
        );

    // 🟢 CRITICAL FIX: Early return for Journey completion
    // If the game has ended OR the player lost, AND we are in a journey, render nothing immediately.
    if (inJourney && (done || isGameEnded || lives <= 0)) {
        return null;
    }

    if (loadingQuestions || error || questions.length === 0) {
        return (
            <div className={styles.background} style={backgroundStyle}>
                <div className={styles.overlay}></div>
                <div className={styles.container}>
                    {loadingQuestions && <LoadingSpinner />}
                    {error && (
                        <>
                            <p className={styles.errorMessage}>Error: {error}</p>
                            <button
                                onClick={loadQuizQuestions}
                                className={styles.proceedButton}
                            >
                                Retry Loading Questions
                            </button>
                        </>
                    )}
                    {questions.length === 0 && !loadingQuestions && (
                        <p className={styles.message}>
                            No questions found. Please seed the database or check backend API.
                        </p>
                    )}
                </div>
            </div>
        );
    }

    const currentQ = questions[current];
    const progressPercent = ((current + 1) / questions.length) * 100;

    return (
        <div className={styles.background} style={backgroundStyle}>
            <div className={styles.overlay}></div>
            <div className={styles.container}>
                <h2 className={styles.title}>📖 Fill in the Blanks</h2>

                {/* --- Instructions --- */}
                {showInstructions ? (
                    <div className={styles.instructionsPopup}>
                        <h3>How to Play Fill in the Blanks:</h3>
                        <ul>
                            <li>An Ayah from the Quran will be displayed with missing words.</li>
                            <li>Select the correct words from the options provided in the correct order.</li>
                            <li>The game ends if you lose all your lives or complete all questions.</li>
                        </ul>
                        <button className={styles.proceedButton} onClick={startQuiz}>
                            Proceed
                        </button>
                    </div>
                ) : done ? (
                    // This block now only handles the STANDALONE result screen.
                    <div className={styles.result}>
                        {showGameOverPopup ? (
                            <>
                                <h3 className={commonGameStyles.gameOverTitle}>
                                    Game Over! 💔
                                </h3>
                                <p className={commonGameStyles.gameOverMessage}>
                                    You ran out of lives.
                                </p>
                                <p className={commonGameStyles.finalScore}>
                                    Your score: {score} / {questions.length}
                                </p>
                            </>
                        ) : (
                            <>
                                <h3>Your Score: {score} / {questions.length}</h3>
                                <p>Great effort! 🌟</p>
                            </>
                        )}

                        <button
                            onClick={() => {
                                resetGameLogicStates();
                                loadQuizQuestions();
                            }}
                            className={styles.proceedButton}
                            style={{ marginTop: "15px" }}
                        >
                            Play Again
                        </button>
                        <button
                            onClick={() => navigate("/home")}
                            className={styles.proceedButton}
                            style={{ marginTop: "15px", marginLeft: "10px" }}
                        >
                            Back to Home
                        </button>
                    </div>
                ) : (
                    <>
                        {/* --- Progress / Lives --- */}
                        <p className={styles.progressText}>
                            Question {current + 1} of {questions.length}
                        </p>
                        <div className={styles.progressWrapper}>
                            <div
                                className={styles.progressBar}
                                style={{ width: `${progressPercent}%` }}
                            />
                        </div>
                        <div className={commonGameStyles.livesContainer}>
                            {[...Array(5)].map((_, i) => (
                                <span
                                    key={i}
                                    className={`${commonGameStyles.heartIcon} ${
                                        i < lives
                                            ? commonGameStyles.filledHeart
                                            : commonGameStyles.emptyHeart
                                    }`}
                                >
                  ❤️
                </span>
                            ))}
                        </div>

                        {!showExplanation && (
                            <div className={styles.timer}>⏱️ {timer} sec</div>
                        )}

                        <p className={styles.ayah}>{currentQ.ayahText}</p>

                        <div className={styles.options}>
                            {currentQ.options.map((word, i) => (
                                <button
                                    key={i}
                                    onClick={() => handleSelect(word)}
                                    className={`${styles.option} ${
                                        selected.includes(word) ? styles.selected : ""
                                    }`}
                                    disabled={
                                        showExplanation ||
                                        showGameOverPopup ||
                                        (selected.length === currentQ.blanks.length &&
                                            !selected.includes(word))
                                    }
                                >
                                    {word}
                                </button>
                            ))}
                        </div>

                        <p className={styles.selectedDisplay}>
                            Selected: {selected.join(", ") || "None"}
                        </p>

                        {!showExplanation && (
                            <button
                                className={styles.submit}
                                onClick={handleSubmit}
                                disabled={
                                    selected.length !== currentQ.blanks.length ||
                                    showGameOverPopup
                                }
                            >
                                Submit
                            </button>
                        )}

                        {/* --- Explanation --- */}
                        {showExplanation && (
                            <div
                                style={{
                                    /* Match HalalVSHaram explanation modal style exactly */
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    backgroundColor: "rgba(0, 0, 0, 0.75)",
                                    padding: "2rem",
                                    borderRadius: "1rem",
                                    width: "90%",
                                    maxWidth: "600px",
                                    textAlign: "center",
                                    color: "#eee",
                                    zIndex: 1000,
                                }}
                            >
                                <p
                                    style={{
                                        fontSize: "1.2rem",
                                        fontWeight: "bold",
                                        color: isCorrect ? "green" : "red",
                                    }}
                                >
                                    {isCorrect ? "✅ Correct, MashaAllah!" : "❌ Incorrect."}
                                </p>

                                {/* Reflection (rendered like HalalVSHaram's explanation) */}
                                {currentQ?.reflection && (
                                    <p>
                                        <strong>Explanation:</strong> {currentQ.reflection}
                                    </p>
                                )}

                                <p>
                                    <strong>Correct Answer:</strong>{" "}
                                    {currentQ.blanks.join(", ")}
                                </p>
                                <p style={{ fontSize: "0.9rem", color: "#ccc" }}>
                                    <em>{currentQ.reference}</em>
                                </p>

                                <button
                                    className={styles.nextButton}
                                    onClick={handleNext}
                                    disabled={showGameOverPopup}
                                >
                                    {current + 1 < questions.length
                                        ? "Next Question"
                                        : "View Results"}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* --- Popups and Feedback (Only in Standalone Mode) --- */}
            <GamePopups
                showStreakPopup={showStreakPopup}
                streakMessage={streakMessage}
                showEncouragementPopup={showEncouragementPopup}
                encouragementMessage={encouragementMessage}
            />

            {!inJourney && (
                <FeedbackModal
                    isOpen={showFeedbackModal}
                    onClose={handleCloseFeedbackModal}
                    onDismissPermanently={handleDismissFeedbackPermanently}
                    formUrl={FEEDBACK_FORM_URL}
                    gameName="Fill in the Blanks"
                />
            )}
        </div>
    );
}

export default FillInTheBlanks;

